import { useUpcomingExternalCalendarEvents } from 'features/external-connections';
import { useGoals } from 'features/goals';
import { useHabits, useRepeatingTasks } from 'features/tasks';
import uniqBy from 'lodash/uniqBy';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useToday } from 'shared/contexts/today';
import { useUser } from 'shared/hooks/use-user';
import { isBetween } from 'shared/lib/date-fns';
import { TimeFormatOptions } from 'shared/types/date-format-options';
import { ID } from 'shared/types/id';
import { DateSectionOptions } from 'shared/types/orderable-section';
import { SortingMode } from 'shared/types/sorting-mode';
import { Task } from 'shared/types/task';
import { TaskType } from 'shared/types/task-base';
import { TaskCardTask } from 'shared/types/task-card-task';
import {
  formatIdDateKey,
  parseIdDateKey,
} from 'shared/utils/format-id-date-key';
import { getCustomOrderBetweenDates } from 'shared/utils/get-custom-order-between-dates';
import { getSortedTasks } from 'shared/utils/get-sorted-tasks';
import { getDueTaskPlanning } from 'shared/utils/get-task-planning';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';
import { mapTaskToTaskCard } from 'shared/utils/mappers/map-task-to-task-card';

import { useUpcomingDeadlineDeadlineTasks } from './use-upcoming-deadline-deadline-tasks';
import { useUpcomingSections } from './use-upcoming-sections';
import { useUpcomingTasks } from './use-upcoming-tasks';

// Helper to parse a date string as local midnight, robust to format
function parseDateAsLocalMidnight(dateString: string) {
  const d = new Date(dateString);

  return new Date(
    d.getUTCFullYear(),
    d.getUTCMonth(),
    d.getUTCDate(),
    0,
    0,
    0,
    0,
  );
}

export const useUpcomingTaskSections = ({
  timeFormat,
}: {
  timeFormat: TimeFormatOptions;
}) => {
  const { t } = useTranslation();
  const today = useToday();

  const todos = useUpcomingTasks();
  const repeatingTasks = useRepeatingTasks({
    uncompletedOnly: true,
    unArchivedOnly: true,
    unFrozenOnly: true,
  });
  const habits = useHabits({
    uncompletedOnly: true,
    unArchivedOnly: true,
    unFrozenOnly: true,
  });
  const deadlineDeadlineTasks = useUpcomingDeadlineDeadlineTasks();
  const externalEvents = useUpcomingExternalCalendarEvents();

  const goals = useGoals();
  const sections = useUpcomingSections();
  const user = useUser();
  const weekStartsOn = getWeekStartsOn(user);

  const tasks = useMemo(
    () =>
      uniqBy(
        [...todos, ...habits, ...repeatingTasks, ...deadlineDeadlineTasks],
        'id',
      ).filter(
        (task) =>
          (task.type !== TaskType.Habit || !task.hideOnTodo) &&
          (task.type !== TaskType.Habit ||
            !user?.settings?.upcoming?.hideHabits) &&
          (!task.completedAt || !user?.settings?.upcoming?.hideCompleted),
      ),
    [
      todos,
      habits,
      repeatingTasks,
      deadlineDeadlineTasks,
      user?.settings?.upcoming?.hideHabits,
      user?.settings?.upcoming?.hideCompleted,
    ],
  );

  const tasksMap = useMemo(
    () =>
      tasks.reduce<Record<ID, Task>>((acc, task) => {
        acc[task.id] = task;
        return acc;
      }, {}),
    [tasks],
  );

  const filledSections = useMemo(() => {
    const usedTasks = new Set<ID>();

    return sections.map(({ endDate, startDate, deadline, type, ...rest }) => {
      const sectionTasks = tasks.reduce<Task[]>((acc, task) => {
        const planning = getDueTaskPlanning(task, {
          date: deadline,
          today,
          weekStartsOn,
          sinceLastEntryOnly: startDate.getTime() === 0,
        });

        const plannedOccurrence = planning.find(({ date }) =>
          isBetween(date, startDate, endDate),
        );
        const deadlineDeadlineOnDate =
          task.deadlineDeadline &&
          isBetween(task.deadlineDeadline, startDate, endDate);

        if (
          (!plannedOccurrence && !deadlineDeadlineOnDate) ||
          (type !== DateSectionOptions.Day && usedTasks.has(task.id))
        ) {
          return acc;
        }

        // add the task as used when the section is not a day anymore
        usedTasks.add(task.id);

        acc.push(task);

        return acc;
      }, []);

      const sectionCustomOrder =
        startDate.getTime() === 0
          ? undefined
          : getCustomOrderBetweenDates(
              user?.settings?.todoSorting ?? {},
              startDate,
              endDate,
            );

      const sortingModes =
        startDate.getTime() === 0
          ? [SortingMode.DueDate]
          : (Array.from(
              new Set([SortingMode.DueDate, user?.settings?.todoSortingMode]),
            ).filter(Boolean) as SortingMode[]);

      const orderedTasks = getSortedTasks(sectionTasks, {
        today,
        weekStartsOn,
        mode: sortingModes,
        order: sectionCustomOrder,
        // overdue tasks need the timeslots to be inverted
        invertedTimeslots: startDate.getTime() === 0,
        allDayFirst: true,
        expectedDueDate: startDate.getTime() === 0 ? undefined : startDate,
        dateRange:
          type !== DateSectionOptions.Day ? { startDate, endDate } : undefined,
      });

      const sectionExternalEvents = user?.settings?.upcoming
        ?.hideExternalCalendarEvents
        ? []
        : externalEvents.filter((externalEvent) =>
            isBetween(
              externalEvent.start.date
                ? parseDateAsLocalMidnight(externalEvent.start.date)
                : new Date(externalEvent.start.dateTime!),
              startDate,
              endDate,
            ),
          );

      return {
        ...rest,
        type,
        deadline,
        startDate,
        endDate,
        items: orderedTasks.map((task) =>
          mapTaskToTaskCard(task, {
            today,
            goals,
            t,
            customId: formatIdDateKey(task.id, deadline),
            showDue: true,
            hideTodayDue: true,
            weekStartsOn,
            timeFormat,
          }),
        ),
        externalEvents: sectionExternalEvents,
      };
    });
  }, [
    sections,
    tasks,
    user?.settings?.todoSorting,
    user?.settings?.todoSortingMode,
    user?.settings?.upcoming?.hideExternalCalendarEvents,
    today,
    weekStartsOn,
    externalEvents,
    goals,
    t,
    timeFormat,
  ]);

  const taskSectionExclusionMap = useMemo(() => {
    const map: Record<ID, ID[]> = {};
    filledSections.forEach((section) => {
      section.items.forEach((task) => {
        // habits and repeating tasks are not allowed to move outside their section when it is not a day
        if (
          section.type !== DateSectionOptions.Day &&
          task.raw.task.schedules?.length
        ) {
          map[task.id] = filledSections.reduce<ID[]>((acc, _section) => {
            if (section.id !== _section.id) {
              acc.push(_section.id);
            }
            return acc;
          }, []);
          return;
        }

        const { id, date } = parseIdDateKey(task.id);
        if (!date) {
          map[task.id] = [];
          return;
        }

        map[task.id] = filledSections.reduce<ID[]>((acc, section) => {
          if (
            section.type !== DateSectionOptions.Day ||
            section.items.some(
              (taskCardTask) => parseIdDateKey(taskCardTask.id).id === id,
            )
          ) {
            acc.push(section.id);
          }
          return acc;
        }, []);
      });
    });

    return map;
  }, [filledSections]);

  const taskCardTasks = useMemo(
    () =>
      filledSections.reduce<TaskCardTask[]>((acc, { items }) => {
        acc.push(...items);
        return acc;
      }, []),
    [filledSections],
  );

  return {
    sections: filledSections,
    tasksMap,
    taskCardTasks,
    taskSectionExclusionMap,
  };
};
